import { colorMetadata } from "../colorMetadata";
import { openDB } from "idb";

const DB_NAME = "colorsDB";
const STORE_NAME = "colors";
const META_STORE = "meta";

const SYNC_KEY = "colorsSyncV1";
const SYNC_VERSION = 3; // Metadata-only catalog; include colorId 0 in full scans.

export async function initDB() {
  return openDB(DB_NAME, 7, {
    upgrade(db, oldVersion, newVersion, transaction) {
      // Rebuild only this remote cache; never materialize legacy full images.
      if (oldVersion > 0 && oldVersion < 7) {
        if (db.objectStoreNames.contains(STORE_NAME)) db.deleteObjectStore(STORE_NAME);
        if (db.objectStoreNames.contains(META_STORE)) transaction.objectStore(META_STORE).clear();
      }
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, {
          keyPath: "colorId"
        });
      }

      if (!db.objectStoreNames.contains(META_STORE)) {
        db.createObjectStore(META_STORE);
      }
    }
  });
}

export function normalizeColor(color) {
  const colorId = color.colorId ?? color.id ?? color.color_id;

  if (colorId == null) {
    console.warn("⚠️ Missing colorId:", color);
    return null;
  }

  return {
    ...colorMetadata(color),
    colorId: colorId
  };
}

export async function saveColors(colors) {
  const db = await initDB();

  const tx = db.transaction([STORE_NAME, META_STORE], "readwrite");
  const store = tx.objectStore(STORE_NAME);
  const changedIds = new Set();

  for (const color of colors) {
    const normalized = normalizeColor(color);

    if (!normalized) continue;

    await store.put(normalized);
    changedIds.add(String(normalized.colorId));
  }

  // A locally created/edited color must not be pruned by an in-flight full sync.
  const meta = tx.objectStore(META_STORE);
  const state = await meta.get(SYNC_KEY);
  if (state?.pending) {
    state.pending.remainingIds = state.pending.remainingIds.filter(id => !changedIds.has(id));
    await meta.put(state, SYNC_KEY);
  }

  await tx.done;
}

export async function syncColorsWithRemote(remoteColors) {
  const db = await initDB();

  const tx = db.transaction(STORE_NAME, "readwrite");
  const store = tx.objectStore(STORE_NAME);

  const remoteIds = new Set(
    remoteColors
      .map((c) => normalizeColor(c))
      .filter(Boolean)
      .map((c) => String(c.colorId))
  );

  const localColors = await store.getAll();

  for (const localColor of localColors) {
    if (!remoteIds.has(String(localColor.colorId))) {
      await store.delete(localColor.colorId);
    }
  }

  for (const color of remoteColors) {
    const normalized = normalizeColor(color);

    if (!normalized) continue;

    await store.put(normalized);
  }

  await tx.done;
}

export async function getAllColorsFromDB() {
  const db = await initDB();

  return db.getAll(STORE_NAME);
}

export async function deleteColorFromDB(colorId) {
  const db = await initDB();

  const tx = db.transaction(STORE_NAME, "readwrite");

  const store = tx.objectStore(STORE_NAME);
  for (const key of await store.getAllKeys()) {
    if (String(key) === String(colorId)) await store.delete(key);
  }

  await tx.done;
}

// Data and checkpoint share a transaction: a page is acknowledged only once its
// colors are durable. The pending cursor survives reloads and network failures.
export function isFullColorSyncDue(lastFullRefresh, now = new Date()) {
  const previous = new Date(lastFullRefresh);
  return !lastFullRefresh || !Number.isFinite(previous.getTime()) ||
    previous.toDateString() !== now.toDateString();
}

export async function beginColorSync({ dueOnly = false } = {}) {
  const db = await initDB();
  const tx = db.transaction([STORE_NAME, META_STORE], "readwrite");
  const meta = tx.objectStore(META_STORE);
  const state = (await meta.get(SYNC_KEY)) || {};
  if (!state.pending) {
    const full = state.lastFullRefreshVersion !== SYNC_VERSION ||
      isFullColorSyncDue(state.lastFullRefresh, new Date(Date.now()));
    if (dueOnly && !full) {
      await tx.done;
      return null;
    }
    state.pending = {
      id: crypto.randomUUID(),
      full,
      version: SYNC_VERSION,
      requestedAt: new Date(Date.now()).toISOString(),
      since: full ? null : state.lastQueryDate,
      afterId: -1,
      upperId: null,
      startedAt: null,
      pages: 0,
      done: false,
      remainingIds: full ? (await tx.objectStore(STORE_NAME).getAllKeys()).map(String) : []
    };
    await meta.put(state, SYNC_KEY);
  }
  await tx.done;
  return state.pending;
}

export async function commitColorSyncPage(expected, batch) {
  const normalized = batch.content.map(normalizeColor);
  if (normalized.some(color => !color)) throw new Error("Invalid color sync response");
  const db = await initDB();
  const tx = db.transaction([STORE_NAME, META_STORE], "readwrite");
  const meta = tx.objectStore(META_STORE);
  const state = await meta.get(SYNC_KEY);
  const pending = state?.pending;
  // Another tab may already have committed this page. Never move backwards.
  if (!pending || pending.id !== expected.id || pending.pages !== expected.pages) {
    await tx.done;
    throw new Error("Color sync advanced in another tab");
  }
  const received = new Set(normalized.map(color => String(color.colorId)));
  const next = {
    ...pending,
    afterId: batch.nextAfterId,
    upperId: pending.upperId ?? batch.upperId,
    startedAt: pending.startedAt ?? batch.startedAt,
    pages: pending.pages + 1,
    done: batch.last,
    remainingIds: pending.remainingIds.filter(id => !received.has(id))
  };
  try {
    for (const color of normalized) await tx.objectStore(STORE_NAME).put(color);
    await meta.put({ ...state, pending: next }, SYNC_KEY);
    await tx.done;
  } catch (error) {
    try { tx.abort(); } catch (_) { /* The transaction may already be aborted. */ }
    await tx.done.catch(() => {});
    throw error;
  }
  return next;
}

export async function finishColorSync(expected) {
  const db = await initDB();
  const tx = db.transaction([STORE_NAME, META_STORE], "readwrite");
  const meta = tx.objectStore(META_STORE);
  const state = await meta.get(SYNC_KEY);
  const pending = state?.pending;
  if (!pending?.done || pending.id !== expected.id) {
    await tx.done;
    throw new Error("Color sync is not complete");
  }
  const store = tx.objectStore(STORE_NAME);
  const removed = new Set(pending.remainingIds);
  for (const key of await store.getAllKeys()) {
    if (removed.has(String(key))) await store.delete(key);
  }
  const completed = {
    lastQueryDate: pending.startedAt,
    lastFullRefresh: pending.full ? (pending.requestedAt || new Date().toISOString()) : state.lastFullRefresh,
    lastFullRefreshVersion: pending.full ? pending.version : state.lastFullRefreshVersion
  };
  await meta.put(completed, SYNC_KEY);
  const colors = await store.getAll();
  await tx.done;
  return { ...completed, colors };
}
